import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardTitle, CardBody } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { X } from 'lucide-react';
import { BillingTransaction } from '../types/admin';
import { format } from 'date-fns';

interface TransactionDetailModalProps {
  transaction: BillingTransaction;
  onClose: () => void;
  formatCents: (cents: number) => string;
  getTransactionTypeLabel: (type: string) => string;
  getTransactionVariant: (type: string) => 'success' | 'error' | 'warning' | 'info' | 'default';
  getStatusVariant: (status: string) => 'success' | 'warning' | 'error' | 'default';
}

const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({
  transaction,
  onClose,
  formatCents,
  getTransactionTypeLabel,
  getTransactionVariant,
  getStatusVariant,
}) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <Card className="w-full max-w-2xl max-h-[80vh] overflow-auto">
      <div className="p-6 border-b border-gray-700 flex items-center justify-between">
        <CardTitle>Transaction Details</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>
      <CardBody>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-gray-400 text-sm">Transaction ID</span>
              <p className="text-white font-mono text-sm">{transaction.id}</p>
            </div>
            <div>
              <span className="text-gray-400 text-sm">Type</span>
              <p className="text-white">
                <Badge variant={getTransactionVariant(transaction.type)}>
                  {getTransactionTypeLabel(transaction.type)}
                </Badge>
              </p>
            </div>
            <div>
              <span className="text-gray-400 text-sm">Status</span>
              <p className="text-white">
                <Badge variant={getStatusVariant(transaction.status)}>
                  {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                </Badge>
              </p>
            </div>
            <div>
              <span className="text-gray-400 text-sm">Account</span>
              <p>
                {transaction.account_name ? (
                  <Link
                    to={`/admin/accounts/${transaction.account_id}`}
                    className="text-primary-400 hover:text-primary-300 hover:underline"
                  >
                    {transaction.account_name}
                  </Link>
                ) : (
                  <span className="text-gray-500">—</span>
                )}
              </p>
            </div>
            <div>
              <span className="text-gray-400 text-sm">Amount</span>
              <p className={transaction.amount_cents < 0 ? 'text-red-400 font-medium' : 'text-green-400 font-medium'}>
                {formatCents(transaction.amount_cents)}
              </p>
            </div>
            <div>
              <span className="text-gray-400 text-sm">Balance After</span>
              <p className="text-white">${(transaction.balance_after_cents / 100).toFixed(2)}</p>
            </div>
            {transaction.analysis_id && (
              <div>
                <span className="text-gray-400 text-sm">Analysis ID</span>
                <p className="text-white font-mono text-sm">{transaction.analysis_id}</p>
              </div>
            )}
            <div>
              <span className="text-gray-400 text-sm">Created</span>
              <p className="text-white">
                {format(new Date(transaction.created_at), 'MMM d, yyyy HH:mm:ss')}
              </p>
            </div>
            {transaction.updated_at && (
              <div>
                <span className="text-gray-400 text-sm">Updated</span>
                <p className="text-white">
                  {format(new Date(transaction.updated_at), 'MMM d, yyyy HH:mm:ss')}
                </p>
              </div>
            )}
          </div>

          <div>
            <span className="text-gray-400 text-sm">Description</span>
            <p className="text-gray-300 mt-1 bg-gray-800 p-3 rounded-md text-sm">
              {transaction.description}
            </p>
          </div>
        </div>
      </CardBody>
    </Card>
  </div>
);

export default TransactionDetailModal;
